import { Capacitor, registerPlugin } from '@capacitor/core';
import { paymentServiceApi } from './api';

export type StoreProvider = 'myket' | 'bazaar';
export type BillingPaymentType = 'subscription' | 'gems';

export interface NativePurchase {
  provider: StoreProvider;
  sku: string;
  productId: string;
  purchaseToken: string;
  orderId?: string;
  purchaseTime?: number;
  purchaseState?: number;
  itemType?: string;
}

export interface StorePaymentIntent {
  order_id: string;
  product_id: string;
  payment_type: BillingPaymentType;
  plan_name?: string | null;
  amount?: string | null;
}

interface BookaBillingPlugin {
  getProvider(): Promise<{ provider: StoreProvider }>;
  initialize(): Promise<void>;
  purchase(options: { sku: string }): Promise<NativePurchase>;
  consume(options: { sku: string; purchaseToken: string }): Promise<{ consumed: boolean; alreadyConsumed?: boolean }>;
  restore(options: { skus: string[] }): Promise<{ purchases: NativePurchase[] }>;
  addListener(
    eventName: 'purchaseStarted',
    listenerFunc: () => void,
  ): Promise<{ remove: () => Promise<void> }>;
  addListener(
    eventName: 'purchaseRecovered',
    listenerFunc: (purchase: NativePurchase) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

const BookaBilling = registerPlugin<BookaBillingPlugin>('BookaBilling');

let providerPromise: Promise<StoreProvider | null> | null = null;
let initializedProvider: StoreProvider | null = null;
const providerPath = (provider: StoreProvider) => provider === 'myket' ? 'myket' : 'bazaar';
const pendingIntentStorageKey = (provider: StoreProvider) => `booka_${provider}_pending_intent`;

export const isNativeStoreBuild = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

export const getStoreProvider = async (): Promise<StoreProvider | null> => {
  if (!isNativeStoreBuild()) return null;
  if (!providerPromise) {
    providerPromise = BookaBilling.getProvider().then(({ provider }) => provider);
  }
  return providerPromise;
};

export const initializeStoreBilling = async (): Promise<StoreProvider | null> => {
  const provider = await getStoreProvider();
  if (!provider) return null;
  if (initializedProvider !== provider) {
    await BookaBilling.initialize();
    initializedProvider = provider;
  }
  return provider;
};

const getProducts = async (provider: StoreProvider) => {
  if (provider !== 'myket' && provider !== 'bazaar') {
    throw new Error('پرداخت بازار هنوز در سرور فعال نشده است');
  }
  const response = await paymentServiceApi.get<{
    provider: StoreProvider;
    products: Record<BillingPaymentType, Record<string, string>>;
    restore_skus?: string[];
  }>(`/payments/${providerPath(provider)}/products`);
  if (!response.success) throw new Error(response.error || response.message || 'خطا در دریافت محصولات پرداخت');
  return response.data;
};

const createStorePaymentIntent = async (
  paymentType: BillingPaymentType,
  planId: number,
  discountCode?: string | null,
): Promise<StorePaymentIntent> => {
  const provider = await initializeStoreBilling();
  if (!provider) throw new Error('Store billing is not available');
  const response = await paymentServiceApi.post<StorePaymentIntent>(`/payments/${providerPath(provider)}/intent`, {
    plan_id: planId,
    payment_type: paymentType,
    discount_code: discountCode || undefined,
  });
  if (!response.success || !response.data?.order_id || !response.data.product_id) {
    throw new Error(response.error || response.message || 'خطا در ایجاد تراکنش پرداخت');
  }
  return response.data;
};

export const purchaseFromStore = async (
  paymentType: BillingPaymentType,
  planId: number,
  discountCode?: string | null,
): Promise<{ provider: StoreProvider; purchase: NativePurchase; intent: StorePaymentIntent }> => {
  const provider = await initializeStoreBilling();
  if (!provider) throw new Error('پرداخت در نسخه وب از این مسیر قابل انجام نیست');

  const intent = await createStorePaymentIntent(paymentType, planId, discountCode);
  const sku = intent.product_id;
  try {
    sessionStorage.setItem(pendingIntentStorageKey(provider), JSON.stringify({
      ...intent,
      plan_id: planId,
      sku,
    }));
  } catch {}
  const purchase = await BookaBilling.purchase({ sku });
  return { provider, purchase, intent };
};

export const verifyStorePurchase = async (
  paymentType: BillingPaymentType,
  planId: number,
  purchase: NativePurchase,
  orderId?: string,
) => {
  if (purchase.provider !== 'myket' && purchase.provider !== 'bazaar') {
    throw new Error('تأیید پرداخت بازار هنوز در بک‌اند فعال نشده است');
  }
  const response = await paymentServiceApi.post(`/payments/${providerPath(purchase.provider)}/verify`, {
    plan_id: planId,
    payment_type: paymentType,
    product_id: purchase.productId || purchase.sku,
    purchase_token: purchase.purchaseToken,
    order_id: orderId || undefined,
  });
  if (!response.success) {
    const error = new Error(
      (response.data as any)?.message || response.error || response.message || 'تأیید پرداخت ناموفق بود'
    ) as Error & { paymentResult?: any };
    error.paymentResult = response.data;
    throw error;
  }
  // The Bazaar catalog currently contains both gems and subscriptions as
  // one-time in-app products (the native bridge uses purchaseProduct). Such
  // products must be consumed after server fulfillment, otherwise Bazaar
  // keeps the SKU owned and rejects the user's next purchase as already owned.
  // Myket is intentionally untouched; its existing consume lifecycle remains
  // in the backend/native Myket implementation.
  if (purchase.provider === 'bazaar') {
    try {
      await BookaBilling.consume({
        sku: purchase.productId || purchase.sku,
        purchaseToken: purchase.purchaseToken,
      });
    } catch (consumeError) {
      // The server has already granted the entitlement. A native consume
      // retry can be performed on the next restore without losing the order.
      console.warn('[Bazaar] Native consume is pending:', consumeError);
    }
  }
  return response.data;
};

export const restoreStorePurchases = async (skus: string[]) => {
  await initializeStoreBilling();
  if (!isNativeStoreBuild()) return { purchases: [] as NativePurchase[] };
  return BookaBilling.restore({ skus });
};

export const restoreAndVerifyStorePurchases = async () => {
  const provider = await initializeStoreBilling();
  if (!provider) return { purchases: [], results: [] };

  const productResponse = await getProducts(provider);
  const products = productResponse.products;
  const skus = productResponse.restore_skus || Object.values(products).flatMap((items) => Object.values(items));
  const restored = await restoreStorePurchases(skus);
  if (!restored.purchases.length) return { ...restored, results: [] };

  let pendingIntent: (StorePaymentIntent & { plan_id: number; sku: string }) | null = null;
  try {
    const raw = sessionStorage.getItem(pendingIntentStorageKey(provider));
    pendingIntent = raw ? JSON.parse(raw) : null;
  } catch {}

  const results = [];
  for (const purchase of restored.purchases) {
    const match = Object.entries(products).flatMap(([paymentType, items]) =>
      Object.entries(items).map(([planId, sku]) => ({ paymentType, planId, sku }))
    ).find((item) => item.sku === (purchase.productId || purchase.sku));
    if (!match) continue;
    const orderId = pendingIntent?.sku === match.sku ? pendingIntent.order_id : undefined;
    const result = await verifyStorePurchase(
      match.paymentType as BillingPaymentType,
      Number(match.planId),
      purchase,
      orderId,
    );
    results.push(result);
  }
  try { sessionStorage.removeItem(pendingIntentStorageKey(provider)); } catch {}
  return { ...restored, results };
};

export const onStorePurchaseStarted = async (listener: () => void) =>
  BookaBilling.addListener('purchaseStarted', listener);
